// Demo persona picker, driven by `GET /api/v1/demo_accounts`. A persona click
// reuses the switch_identity call /users login-as uses.
import {
  CURRENT_USER_OUTLINE,
  DemoAccountTile,
} from '@/components/ap/layout/demoSwitcher/DemoAccountTile';
import {
  groupIcon,
  JURISDICTION_ICON,
  orgIcon,
  roleIcon,
} from '@/components/ap/layout/demoSwitcher/demoAccountIcons';
import { useCurrentUser } from '@/hooks/dataHelpers';
import { toTitleCase } from '@/lib/commonHelpers';
import { useSwitchIdentity } from '@/lib/hooks/useSwitchIdentity';
import type {
  DemoAccountGroup,
  DemoAccountsResponse,
  DemoOrg,
  DemoOrgKind,
  DemoPersona,
} from '@/types/demo';
import { AltArrowRightLinear, InfoCircleLinear, MagniferLinear } from '@assessprep/icons/solar';
import { useCustom } from '@refinedev/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/cn';

export interface DemoAccountsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface JurisdictionBucket {
  key: string;
  name: string;
  orgs: DemoOrg[];
}

const TILE_GRID = 'grid gap-3 sm:grid-cols-2 lg:grid-cols-3';

const ORG_ROW = 'border bg-card text-card-foreground';

const JurisdictionIcon = JURISDICTION_ICON;

// An org with no jurisdiction still needs a bucket key; backend keys are state
// codes, so the empty string can never collide with one.
const NO_JURISDICTION = '';

// Reading order of the count line on a jurisdiction row: parents first.
const KIND_COUNT_KEYS: [DemoOrgKind, string][] = [
  ['network', 'V4Shell.demo_count_network'],
  ['district', 'V4Shell.demo_count_district'],
  ['board', 'V4Shell.demo_count_board'],
  ['school', 'V4Shell.demo_count_school'],
];

// A chip shows the name, so the label loses its scope word: "School teacher"
// reads "Teacher". The tooltip keeps the whole label.
const shortRole = (label: string) => toTitleCase(label.slice(label.lastIndexOf(' ') + 1));

// `label` is the shown role ("School admin"); `role` is the machine role
// ("admin"). Both are searchable so typing what you see matches.
const matchesPersona = (persona: DemoPersona, needle: string) =>
  `${persona.name} ${persona.label} ${persona.role} ${persona.email}`
    .toLowerCase()
    .includes(needle);

const jurisdictionBuckets = (group: DemoAccountGroup | null): JurisdictionBucket[] => {
  const buckets: JurisdictionBucket[] = [];
  group?.orgs.forEach((org) => {
    const key = org.jurisdiction?.key ?? NO_JURISDICTION;
    const bucket = buckets.find((entry) => entry.key === key);
    if (bucket) bucket.orgs.push(org);
    else buckets.push({ key, name: org.jurisdiction?.name ?? '', orgs: [org] });
  });
  return buckets;
};

// An org whose parent is outside the list is a root of its own.
const treeRoots = (orgs: DemoOrg[]) =>
  orgs.filter((org) => !org.parent_slug || !orgs.some((entry) => entry.slug === org.parent_slug));

export const DemoAccountsDialog = ({ open, onOpenChange }: DemoAccountsDialogProps) => {
  const { t } = useTranslation();
  const { switchIdentity, pendingId } = useSwitchIdentity();
  // Null-safe (APL-12040): a switch nulls the user while the dialog closes.
  const currentUserEmail: string | undefined = useCurrentUser()?.email;
  const [groupKey, setGroupKey] = useState<string | null>(null);
  const [jurisdictionKey, setJurisdictionKey] = useState<string | null>(null);
  const [collapsedSlugs, setCollapsedSlugs] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const openedAtCurrentOrg = useRef(false);

  const { data, isLoading, isError, refetch } = useCustom<DemoAccountsResponse>({
    url: 'demo_accounts',
    method: 'get',
    // The dialog renders its own error state; a toast would say it twice.
    errorNotification: false,
    queryOptions: { enabled: open },
  });

  const groups = useMemo(() => data?.data.groups ?? [], [data]);
  const currentSlug = data?.data.current_slug ?? null;

  // The dialog opens on the org the signed-in user belongs to. Once per open:
  // a background refetch must not pull the reader off the tab they picked.
  useEffect(() => {
    if (!open) {
      openedAtCurrentOrg.current = false;
      return;
    }
    if (openedAtCurrentOrg.current || groups.length === 0) return;
    openedAtCurrentOrg.current = true;
    const home = groups.find((group) => group.orgs.some((org) => org.slug === currentSlug));
    const org = home?.orgs.find((entry) => entry.slug === currentSlug);
    if (!home || !org) return;
    setGroupKey(home.key);
    setJurisdictionKey(org.jurisdiction?.key ?? NO_JURISDICTION);
  }, [open, groups, currentSlug]);

  const activeGroup = groups.find((group) => group.key === groupKey) ?? groups[0] ?? null;
  const buckets = jurisdictionBuckets(activeGroup);
  // One jurisdiction (or none at all) carries no choice — the rail is dropped.
  // A group under one root is one tree: bucketing it by jurisdiction would show
  // a network apart from its own districts, so the whole tree takes the pane.
  const hasRail = buckets.length > 1 && treeRoots(activeGroup?.orgs ?? []).length > 1;
  const activeBucket =
    buckets.find((bucket) => bucket.key === jurisdictionKey) ?? buckets[0] ?? null;
  const paneOrgs = hasRail ? activeBucket?.orgs ?? [] : activeGroup?.orgs ?? [];
  // A group that is one ready org carries no tree — its personas are the pane.
  const soloOrg =
    activeGroup && activeGroup.orgs.length === 1 && activeGroup.orgs[0].status === 'ready'
      ? activeGroup.orgs[0]
      : null;

  const needle = search.trim().toLowerCase();
  // The tree gives a coming-soon org no persona chips; the search must not
  // offer what the tree blocks.
  const personaMatches: DemoPersona[] = needle
    ? groups.flatMap((group) =>
        group.orgs
          .filter((org) => org.status !== 'coming_soon')
          .flatMap((org) => org.personas.filter((persona) => matchesPersona(persona, needle)))
      )
    : [];

  const handleOpenChange = (next: boolean) => {
    if (pendingId) return;
    if (!next) {
      setGroupKey(null);
      setJurisdictionKey(null);
      setCollapsedSlugs([]);
      setSearch('');
    }
    onOpenChange(next);
  };

  const handleSelectGroup = (key: string) => {
    setGroupKey(key);
    setJurisdictionKey(null);
  };

  const handleToggleOrg = (slug: string) =>
    setCollapsedSlugs((slugs) =>
      slugs.includes(slug) ? slugs.filter((entry) => entry !== slug) : [...slugs, slug]
    );

  const handleSelectPersona = (persona: DemoPersona) => {
    if (pendingId) return;
    // Close first (v1 parity — the switcher closed on click regardless of
    // outcome); switchIdentity reports success or failure by toast.
    handleOpenChange(false);
    switchIdentity({ email: persona.email });
  };

  const bucketSummary = (bucket: JurisdictionBucket) => {
    if (bucket.orgs.every((org) => org.status === 'coming_soon'))
      return t('V4Shell.demo_coming_soon');
    const counted = KIND_COUNT_KEYS.flatMap(([kind, key]) => {
      const count = bucket.orgs.filter((org) => org.kind === kind).length;
      return count > 0 ? [t(key, { count })] : [];
    });
    const rest = bucket.orgs.filter(
      (org) => !KIND_COUNT_KEYS.some(([kind]) => kind === org.kind)
    ).length;
    if (rest > 0) counted.push(t('V4Shell.demo_count_organization', { count: rest }));
    return counted.join(' · ');
  };

  const emptyState = (
    <div
      className="flex flex-col items-center rounded-lg border px-6 py-10 text-center"
      data-testid="v4-demo-bar-empty"
    >
      <p className="text-sm font-medium">{t('DataGrid.no_data')}</p>
    </div>
  );

  const renderPersona = (persona: DemoPersona) => (
    <DemoAccountTile
      key={persona.email}
      icon={roleIcon(persona.role)}
      title={persona.name}
      titleBadge={<Badge variant="secondary">{persona.label}</Badge>}
      isCurrentUser={persona.email === currentUserEmail}
      // break-all lets the clamp end an unbreakable address with an ellipsis.
      summary={<span className="break-all">{persona.email}</span>}
      disabled={!!pendingId}
      onClick={() => handleSelectPersona(persona)}
      data-testid="v4-demo-bar-user-option"
      data-demo-user={persona.email}
    />
  );

  const renderPersonaChip = (persona: DemoPersona) => {
    const Icon = roleIcon(persona.role);
    const isCurrentUser = persona.email === currentUserEmail;
    return (
      <Button
        key={persona.email}
        type="button"
        variant="outline"
        size="sm"
        className={cn('max-w-full', isCurrentUser && CURRENT_USER_OUTLINE)}
        title={`${persona.name} · ${persona.label} · ${persona.email}`}
        aria-current={isCurrentUser || undefined}
        disabled={!!pendingId}
        onClick={() => handleSelectPersona(persona)}
        data-testid="v4-demo-bar-persona-chip"
        data-demo-user={persona.email}
      >
        <Icon aria-hidden="true" />
        <span className="min-w-0 truncate">
          {persona.name}
          <span className="font-normal text-muted-foreground"> · {shortRole(persona.label)}</span>
        </span>
      </Button>
    );
  };

  // Every org of the pane is a row; schools nest inside the district that
  // names them in `parent_slug`, districts inside their network.
  const renderOrgRow = (org: DemoOrg, orgs: DemoOrg[], nested = false) => {
    const children = orgs.filter((entry) => entry.parent_slug === org.slug);
    const isComingSoon = org.status === 'coming_soon';
    const isCurrent = org.slug === currentSlug;
    const isExpanded = !collapsedSlugs.includes(org.slug);
    const Icon = orgIcon(org.kind, org.title);
    return (
      <Collapsible
        key={org.slug}
        open={isExpanded}
        onOpenChange={() => handleToggleOrg(org.slug)}
        className={cn(
          'rounded-lg',
          // Only the outermost row draws a card; the indent carries the
          // nesting, so a network of districts of schools never stacks cards.
          !nested && ORG_ROW,
          isComingSoon && 'opacity-70'
        )}
        aria-disabled={isComingSoon || undefined}
        data-testid="v4-demo-bar-org-row"
        data-demo-org={org.slug}
        data-demo-org-status={org.status}
        data-demo-org-current={isCurrent || undefined}
      >
        <div className="flex flex-col gap-2 p-3">
          <div className="flex min-w-0 items-center gap-2">
            {children.length > 0 ? (
              <CollapsibleTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="shrink-0"
                  aria-label={t(isExpanded ? 'CommonText.collapse' : 'CommonText.expand')}
                >
                  <AltArrowRightLinear
                    aria-hidden="true"
                    className={cn(
                      'transition-transform',
                      isExpanded ? 'rotate-90' : 'rtl:rotate-180'
                    )}
                  />
                </Button>
              </CollapsibleTrigger>
            ) : (
              <span className="size-8 shrink-0" aria-hidden="true" />
            )}
            <span
              className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-ap-primary-a10 text-primary"
              aria-hidden="true"
            >
              <Icon className="size-4" />
            </span>
            <span className="min-w-0 truncate text-sm font-medium">{org.title}</span>
            {org.pitch && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className="size-5 shrink-0 text-muted-foreground"
                    aria-label={t('CommonText.more_information')}
                    data-testid="v4-demo-bar-org-info"
                  >
                    <InfoCircleLinear aria-hidden="true" className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">{org.pitch}</TooltipContent>
              </Tooltip>
            )}
            {isComingSoon && (
              <Badge variant="secondary" className="shrink-0">
                {t('V4Shell.demo_coming_soon')}
              </Badge>
            )}
          </div>
          {!isComingSoon && org.personas.length > 0 && (
            // ps-20 starts the chips under the org name: toggle + icon + gaps.
            <div className="flex flex-wrap gap-1.5 ps-20">
              {org.personas.map(renderPersonaChip)}
            </div>
          )}
        </div>
        {children.length > 0 && (
          <CollapsibleContent>
            <div
              className="ms-6 grid gap-2 pb-3 pe-3 ps-4"
              data-testid="v4-demo-bar-org-children"
              data-demo-org-parent={org.slug}
            >
              {children.map((child) => renderOrgRow(child, orgs, true))}
            </div>
          </CollapsibleContent>
        )}
      </Collapsible>
    );
  };

  const renderOrgTree = (orgs: DemoOrg[]) =>
    treeRoots(orgs).map((root) => renderOrgRow(root, orgs));

  const renderPane = () => {
    if (soloOrg)
      return soloOrg.personas.length === 0 ? (
        emptyState
      ) : (
        <div className="flex flex-col gap-3">
          {soloOrg.pitch && <p className="text-sm text-muted-foreground">{soloOrg.pitch}</p>}
          <div className={TILE_GRID}>{soloOrg.personas.map(renderPersona)}</div>
        </div>
      );
    if (paneOrgs.length === 0) return emptyState;
    return <div className="grid gap-2">{renderOrgTree(paneOrgs)}</div>;
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className={TILE_GRID} data-testid="v4-demo-bar-loading">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-20 w-full rounded-md" />
          ))}
        </div>
      );
    }

    if (isError) {
      return (
        <div
          role="alert"
          className="flex flex-col items-center gap-3 rounded-lg border border-ap-destructive-a30 bg-ap-destructive-a5 px-6 py-10 text-center"
          data-testid="v4-demo-bar-error"
        >
          <p className="text-sm font-medium text-destructive">{t('DataGrid.load_error')}</p>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            {t('CommonText.try_again')}
          </Button>
        </div>
      );
    }

    if (needle) {
      return personaMatches.length === 0 ? (
        emptyState
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto" data-testid="v4-demo-bar-list">
          <div className={TILE_GRID}>{personaMatches.map(renderPersona)}</div>
        </div>
      );
    }

    if (!activeGroup) return emptyState;

    return (
      <Tabs value={activeGroup.key} onValueChange={handleSelectGroup} className="min-h-0 flex-1">
        <TabsList variant="line" className="w-full shrink-0 justify-start overflow-x-auto">
          {groups.map((group) => {
            const Icon = groupIcon(group.key);
            return (
              <TabsTrigger
                key={group.key}
                value={group.key}
                className="flex-none"
                data-testid="v4-demo-bar-tab"
                data-demo-group={group.key}
              >
                <Icon aria-hidden="true" />
                {group.title}
              </TabsTrigger>
            );
          })}
        </TabsList>
        <TabsContent
          value={activeGroup.key}
          className="flex min-h-0 flex-col gap-3 lg:flex-row lg:gap-4"
        >
          {hasRail && (
            <nav
              aria-label={t('V4Shell.demo_choose_state')}
              className="flex shrink-0 gap-2 overflow-x-auto border-b pb-2 lg:w-56 lg:flex-col lg:gap-1 lg:overflow-x-visible lg:overflow-y-auto lg:border-b-0 lg:border-e lg:pb-0 lg:pe-2"
            >
              {buckets.map((bucket) => {
                const isActive = bucket.key === activeBucket?.key;
                return (
                  <Button
                    key={bucket.key}
                    type="button"
                    variant={isActive ? 'secondary' : 'ghost'}
                    className="h-auto shrink-0 justify-start py-2 text-start lg:w-full"
                    aria-current={isActive || undefined}
                    onClick={() => setJurisdictionKey(bucket.key)}
                    data-testid="v4-demo-bar-jurisdiction"
                    data-demo-jurisdiction={bucket.key}
                  >
                    <JurisdictionIcon aria-hidden="true" className="shrink-0" />
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate">
                        {bucket.name || t('V4Shell.demo_no_jurisdiction')}
                      </span>
                      <span className="hidden truncate text-xs font-normal text-muted-foreground lg:block">
                        {bucketSummary(bucket)}
                      </span>
                    </span>
                  </Button>
                );
              })}
            </nav>
          )}
          <div className="min-h-0 flex-1 overflow-y-auto" data-testid="v4-demo-bar-list">
            {renderPane()}
          </div>
        </TabsContent>
      </Tabs>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="flex h-[80vh] flex-col sm:max-w-5xl"
        data-testid="v4-demo-bar-dialog"
      >
        <TooltipProvider>
          <DialogHeader>
            <DialogTitle>{t('V4Shell.demo_switch_user')}</DialogTitle>
            <DialogDescription>{t('V4Shell.demo_switch_user_description')}</DialogDescription>
          </DialogHeader>

          <div className="relative">
            <MagniferLinear
              aria-hidden="true"
              className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('V4Shell.demo_search_placeholder')}
              className="ps-8"
              data-testid="v4-demo-bar-search"
            />
          </div>

          {renderBody()}
        </TooltipProvider>
      </DialogContent>
    </Dialog>
  );
};
